// Pure helpers for the rate-ladder editor: group tiers into ladders and
// re-link the preemptive chain after reordering. No React — unit-testable.
//
// A "ladder" is one escalation chain: tiers that share a group (Female / Male
// / VIP / …) where each tier preempts the one below it (sells out → next
// opens at a higher price). We derive the group from the tier name suffix
// ("… - Female") with a VIP special-case, falling back to "General". The
// preemptive chain within a ladder is implied by display order, so reordering
// rewrites both sort_index and preemptive_ref — ops never touches refs.

export function groupKeyOf(name) {
  const n = String(name || '');
  if (/\bvip\b/i.test(n)) return 'VIP';
  const m = n.match(/\s[-–]\s*([^-–]+?)\s*$/); // trailing " - Female" / " – Male"
  if (m) return m[1].trim();
  return 'General';
}

function slugKey(key) {
  return String(key).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'tier';
}

// tiers[] -> [{ key, label, tiers: [...] }], ladders ordered by their first
// tier's sort_index, tiers within a ladder ordered by sort_index then price.
export function groupIntoLadders(tiers) {
  const map = new Map();
  (tiers || []).forEach((t) => {
    const key = groupKeyOf(t.name);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(t);
  });
  const groups = [];
  for (const [key, ts] of map) {
    ts.sort((a, b) => (a.sort_index ?? 0) - (b.sort_index ?? 0) || (Number(a.price) || 0) - (Number(b.price) || 0));
    groups.push({ key, label: key, tiers: ts });
  }
  groups.sort((a, b) => (a.tiers[0]?.sort_index ?? 0) - (b.tiers[0]?.sort_index ?? 0));
  return groups;
}

// Flatten grouped ladders back to a tiers[], re-sequencing only `sort_index`
// from display order. Crucially it does NOT rewrite `preemptive_ref`: the
// gating is a TREE (one tier can release several; chains cross sections), so
// forcing a sequential "each preempts the one above" would corrupt it. Gating
// is edited explicitly, not implied by row order. Strips editor-only helper
// fields (keys starting with "_").
export function applyOrder(groups) {
  const out = [];
  for (const g of groups) {
    for (const t of g.tiers) {
      const clean = {};
      for (const [k, v] of Object.entries(t)) if (!k.startsWith('_')) clean[k] = v;
      out.push({ ...clean, sort_index: out.length });
    }
  }
  return out;
}

// Build the next tier for a ladder so "+ tier" is one click: continue the
// "Tier N" numbering and the price escalation, cloning cap/state/etc. from the
// last tier. So a Female ladder ending at "Tier 5 - Female $100" yields
// "General Admission Tier 6 - Female" at the next price step.
export function nextTierTemplate(group) {
  const tiers = group?.tiers || [];
  const last = tiers[tiers.length - 1] || {};

  // Highest existing "Tier N" in this ladder.
  const nums = tiers
    .map((t) => { const m = String(t.name || '').match(/tier\s*(\d+)/i); return m ? Number(m[1]) : null; })
    .filter((n) => n != null);
  const maxN = nums.length ? Math.max(...nums) : 0;

  let name;
  if (maxN > 0) {
    // Clone the most recent "Tier X" name and bump the number.
    const base = [...tiers].reverse().find((t) => /tier\s*\d+/i.test(t.name || '')) || last;
    name = String(base.name).replace(/tier\s*\d+/i, `Tier ${maxN + 1}`);
  } else if (group?.key && group.key !== 'General' && group.key !== 'VIP') {
    name = `General Admission Tier 1 - ${group.key}`;
  } else {
    name = 'New Tier';
  }

  // Price: continue the escalation — step = gap between the last two tiers,
  // else +10 off the last, else 0.
  const prices = tiers.map((t) => Number(t.price) || 0);
  let price = 0;
  if (prices.length >= 2) {
    const step = prices[prices.length - 1] - prices[prices.length - 2];
    price = prices[prices.length - 1] + (step > 0 ? step : 10);
  } else if (prices.length === 1) {
    price = prices[0] + 10;
  }

  return {
    name,
    description: '',
    price,
    capacity: last.capacity ?? 50,
    state: 'ACTIVE',
    client_group: last.client_group || 'EVERYWHERE',
    type: price > 0 ? 'PAID_TICKET' : 'FREE_TICKET',
    release_offset: last.release_offset ?? 86400,
    max_tickets: last.max_tickets ?? null,
    min_tickets: last.min_tickets ?? null,
    promotion_pill: null,
    sale_start: null,
    sale_end: null,
    max_redemptions: null,
    redemption_message: null,
    redemption_message_active: false,
  };
}

// A plain, custom tier (the "+ tier" button) — no "Tier N" numbering, neutral
// name the user fills in, sensible defaults cloned from the section's last tier.
// Not auto-chained (preemptive_ref set by the caller / left null).
export function blankTierTemplate(group) {
  const tiers = group?.tiers || [];
  const last = tiers[tiers.length - 1] || {};
  return {
    name: '',
    description: '',
    price: 0,
    capacity: last.capacity ?? 50,
    state: 'ACTIVE',
    client_group: last.client_group || 'EVERYWHERE',
    type: 'FREE_TICKET', // price 0 → FREE_TICKET (flips to PAID once priced)
    release_offset: last.release_offset ?? 86400,
    max_tickets: null,
    min_tickets: null,
    promotion_pill: null,
    sale_start: null,
    sale_end: null,
    max_redemptions: null,
    redemption_message: null,
    redemption_message_active: false,
  };
}

// Split a tier's misleading raw `state` into the two things ops actually
// want: visibility (the configured on-sale setting, plain-worded) and
// sold-out (derived from live sold vs capacity — independent of state).
const VISIBILITY_LABEL = { ACTIVE: 'On sale', INACTIVE: 'Off', HIDDEN: 'Hidden', LOCKED: 'Code only' };
export function tierStatus({ state, sold, capacity, hasSales } = {}) {
  const visibility = VISIBILITY_LABEL[String(state || 'ACTIVE').toUpperCase()] || String(state || '');
  const cap = Number(capacity);
  const s = sold == null ? null : Number(sold);
  const known = s != null && Number.isFinite(cap) && cap > 0; // live data present
  return {
    visibility,
    soldOut: known ? s >= cap : false,
    remaining: known ? Math.max(0, cap - s) : null,
    sold: s,
    hasSold: (s != null && s > 0) || !!hasSales,
  };
}

// Map live Universe rates into our tier shape, preserving the gate links
// (opensAfter name → preemptive_ref). Used when an event has no captured
// ladder of its own (orphan / default) so the editor still shows what's
// actually live; the first edit then persists these as the override.
export function liveRatesToTiers(liveRates) {
  const rates = (liveRates || []).slice().sort((a, b) => (a.sort_index ?? 0) - (b.sort_index ?? 0));
  const refByName = {};
  rates.forEach((r, i) => { refByName[r.name] = `${slugKey(r.name).slice(0, 20)}-${i}`; });
  return rates.map((r, i) => ({
    // Universe rate id — carried so the full-payload save targets rates by id
    // (rename-safe); new tiers added in the editor have no id (Universe creates).
    id: r.id ?? null,
    ref: refByName[r.name],
    name: r.name,
    description: r.description ?? '',
    price: r.price,
    capacity: r.capacity,
    state: (r.state || 'ACTIVE').toUpperCase(),
    sort_index: r.sort_index ?? i,
    // Faithfulness: a live-seeded ladder must keep the rate type (Free vs
    // paid) and release window, or the next push rewrites them (T.I incident).
    type: r.type || 'PAID_TICKET',
    release_offset: r.release_offset ?? null,
    // Sourced from manageTickets via /live (write-vocabulary). Carried so the
    // full-payload push round-trips them instead of defaulting/wiping.
    client_group: r.client_group || 'EVERYWHERE',
    access_key: r.access_key ?? null,
    // Per-order limits + promo label (multiverse REST: max_tickets/min_tickets/
    // promotion_pill). Omitting these is what left "max per order" blank.
    max_tickets: r.max_tickets ?? null,
    min_tickets: r.min_tickets ?? null,
    promotion_pill: r.promotion_pill ?? null,
    // Sale window (multiverse start_stamp/end_stamp, epoch secs → null when unset).
    sale_start: r.sale_start ?? null,
    sale_end: r.sale_end ?? null,
    // Redemption / door-alert (merged from manageTickets — not in multiverse).
    max_redemptions: r.max_redemptions ?? null,
    redemption_message: r.redemption_message ?? null,
    redemption_message_active: r.redemption_message_active ?? false,
    preemptive_ref: r.opensAfter ? (refByName[r.opensAfter] || null) : null,
  }));
}

export function priceRange(tiers) {
  const ps = (tiers || []).map((t) => Number(t.price) || 0);
  if (!ps.length) return '';
  const min = Math.min(...ps); const max = Math.max(...ps);
  return min === max ? `$${min}` : `$${min}–$${max}`;
}
